import React, { useState, useRef } from "react";
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  HStack,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Avatar,
  Switch,
  Tooltip,
  Select,
  Icon,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { FaPlus, FaUser, FaUserShield, FaUserTie, FaUserGraduate } from "react-icons/fa";
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getUsers, toggleUserStatus, createUser, updateUser, getUserById, deleteUser } from '../services/userService';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import CustomModal from '../components/Modal';
import UserForm from '../components/forms/UserForm';
import JalaliDateDisplay from '../components/JalaliDateDisplay';

function Users() {
  const textColor = useColorModeValue("gray.700", "white");
  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  
  // State برای جستجو، فیلتر و modal
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // فیلتر وضعیت
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const formRef = useRef(null);
  
  // React Query hooks
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  
  // دریافت لیست کاربران (همیشه با pagination و جستجو/فیلتر)
  const {
    data: usersData,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['users', currentPage, searchTerm, roleFilter, statusFilter],
    () => getUsers({
      page: currentPage,
      limit: 10,
      role: roleFilter || undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      search: searchTerm || undefined
    }),
    {
      keepPreviousData: true,
      staleTime: 2 * 60 * 1000, // 2 دقیقه
    }
  );

  // Mutation برای تغییر وضعیت کاربر
  const toggleUserStatusMutation = useMutation(toggleUserStatus, {
    onSuccess: () => {
      toast.success('وضعیت کاربر با موفقیت تغییر یافت');
      queryClient.invalidateQueries(['users']);
    },
    onError: (error) => {
      toast.error(error.message || 'خطا در تغییر وضعیت کاربر');
    }
  });

  // Mutation برای ایجاد کاربر
  const createUserMutation = useMutation(createUser, {
    onSuccess: () => {
      toast.success('کاربر با موفقیت ایجاد شد');
      queryClient.invalidateQueries(['users']);
      setIsModalOpen(false);
      setEditingUser(null);
    },
    onError: (error) => {
      toast.error(error.message || 'خطا در ایجاد کاربر');
    }
  });

  // Mutation برای بروزرسانی کاربر
  const updateUserMutation = useMutation(updateUser, {
    onSuccess: () => {
      toast.success('کاربر با موفقیت بروزرسانی شد');
      queryClient.invalidateQueries(['users']);
      setIsModalOpen(false);
      setEditingUser(null);
    },
    onError: (error) => {
      toast.error(error.message || 'خطا در بروزرسانی کاربر');
    }
  });

  // Mutation برای حذف کاربر
  const deleteUserMutation = useMutation(deleteUser, {
    onSuccess: () => {
      toast.success('کاربر با موفقیت حذف شد');
      queryClient.invalidateQueries(['users']);
    },
    onError: (error) => {
      toast.error(error.message || 'خطا در حذف کاربر');
    }
  });

  // تابع تغییر وضعیت کاربر
  const handleToggleUserStatus = (userId) => {
    // پیدا کردن کاربر مورد نظر
    const targetUser = users.find(user => (user._id || user.id) === userId);
    
    // بررسی دسترسی: مدیر نمی‌تواند وضعیت مدیر کل را تغییر دهد
    if (currentUser?.role === 'admin' && targetUser?.role === 'manager') {
      toast.error('شما اجازه تغییر وضعیت مدیر کل را ندارید');
      return;
    }
    
    toggleUserStatusMutation.mutate(userId);
  };

  // تابع باز کردن modal برای افزودن
  const handleAddUser = () => {
    setEditingUser(null);
    setIsModalOpen(true);
  };

  // تابع باز کردن modal برای ویرایش
  const handleEditUser = async (user) => {
    // بررسی دسترسی: مدیر نمی‌تواند مدیر کل را ویرایش کند
    if (currentUser?.role === 'admin' && user.role === 'manager') {
      toast.error('شما اجازه ویرایش مدیر کل را ندارید');
      return;
    }

    try {
      const fullUserData = await getUserById(user._id || user.id);
      const fullUser = fullUserData.data.user;
      
      // تبدیل نام کامل به نام و نام خانوادگی
      const nameParts = fullUser.name ? fullUser.name.split(' ') : ['', ''];
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      const cleanUser = {
        _id: String(fullUser._id), // تبدیل به string
        id: fullUser.id ? String(fullUser.id) : null,
        firstName: firstName,
        lastName: lastName,
        name: fullUser.name,
        email: fullUser.email,
        phone: fullUser.phone,
        role: fullUser.role,
        specialty: fullUser.specialty || 'general',
        licenseNumber: fullUser.licenseNumber || '',
        experience: fullUser.experience || '',
        education: fullUser.education || '',
        bio: fullUser.bio || '',
        hourlyRate: fullUser.hourlyRate || 0,
        sessionDuration: fullUser.sessionDuration || 60,
        active: fullUser.isActive !== false
      };
      
      setEditingUser(cleanUser);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error fetching user details:', error);
      toast.error('خطا در دریافت اطلاعات کاربر');
    }
  };

  // تابع بستن modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
  };

  // تابع ارسال فرم
  const handleSubmitForm = (data) => {
    const cleanData = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone,
      role: data.role,
      specialty: data.specialty || '',
      licenseNumber: data.licenseNumber || '',
      experience: data.experience || '',
      education: data.education || '',
      bio: data.bio || '',
      hourlyRate: data.hourlyRate ? parseFloat(data.hourlyRate) : 0,
      sessionDuration: data.sessionDuration ? parseInt(data.sessionDuration) : 60,
      active: data.active !== false
    };

    if (editingUser) {
      // ویرایش کاربر موجود
    
      
      let userId = null;
      
      // بررسی اینکه آیا _id یا id وجود دارد
      if (editingUser._id) {
        userId = editingUser._id;
       
      } else if (editingUser.id) {
        userId = editingUser.id;
       
      }
      
    
      
      if (!userId) {
        toast.error('شناسه کاربر یافت نشد');
        return;
      }
      
      // اطمینان از اینکه userId یک string معتبر است
      const userIdString = String(userId).trim();
    
      
      if (!userIdString || userIdString === 'undefined' || userIdString === 'null' || userIdString === '[object Object]') {
        toast.error('شناسه کاربر نامعتبر است');
        return;
      }
      
     
      
      // ارسال به صورت جداگانه برای اطمینان
      const mutationData = {
        userId: userIdString,
        userData: cleanData
      };
      
      
      updateUserMutation.mutate(mutationData);
    } else {
      // افزودن کاربر جدید
      createUserMutation.mutate(cleanData);
    }
  };

  // تابع جستجو
  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  // تابع فیلتر بر اساس نقش
  const handleRoleFilter = (value) => {
    setRoleFilter(value);
    setCurrentPage(1);
  };

  // تابع فیلتر بر اساس وضعیت
  const handleStatusFilter = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  // تابع تبدیل نقش به رنگ
  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'red';
      case 'manager':
        return 'purple';
      case 'consultant':
        return 'blue';
      case 'client':
        return 'green';
      default:
        return 'gray';
    }
  };

  // تابع تبدیل نقش به فارسی
  const getRoleText = (role) => {
    switch (role) {
      case 'admin':
        return 'مدیر';
      case 'manager':
        return 'مدیر کل';
      case 'consultant':
        return 'مشاور';
      case 'client':
        return 'مراجع';
      default:
        return 'نامشخص';
    }
  };

  // تابع دریافت آیکون نقش
  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return FaUserShield;
      case 'manager':
        return FaUserShield;
      case 'consultant':
        return FaUserTie;
      case 'client':
        return FaUserGraduate;
      default:
        return FaUser;
    }
  };

  // داده‌های کاربران و pagination
  const users = usersData?.data?.users || usersData?.users || usersData?.data || usersData || [];
  const pagination = usersData?.data?.pagination || {};
  const { currentPage: page = 1, totalPages = 1, totalItems = 0, itemsPerPage = 10 } = pagination;

  // اگر در حال بارگذاری است
  if (isLoading) {
    return (
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            <Flex justify="center" align="center" minH="400px">
              <Spinner size="xl" color="teal.500" />
              <Text mr={4} fontSize="lg" color={textColor}>
                در حال بارگذاری کاربران...
              </Text>
            </Flex>
          </Box>
        </Box>
      </Flex>
    );
  }

  // اگر خطا رخ داده است
  if (error) {
    return (
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <AlertTitle>خطا در بارگذاری داده‌ها!</AlertTitle>
              <AlertDescription>
                {error.message || 'خطا در ارتباط با سرور'}
              </AlertDescription>
            </Alert>
            <Button 
              mt={4} 
              colorScheme="teal" 
              onClick={() => refetch()}
              isLoading={isLoading}
            >
              تلاش مجدد
            </Button>
          </Box>
        </Box>
      </Flex>
    );
  }

  return (
    <>
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            <Box p="20px 0px 35px 0px">
              <Flex direction="column">
                <Text fontSize="lg" color={textColor} fontWeight="bold" me="auto">
                  مدیریت کاربران
                </Text>
                <Text fontSize="md" fontWeight="400" me="auto" color="gray.400">
                  مشاهده و مدیریت کاربران سیستم
                </Text>
              </Flex>
            </Box>
            <Box px="0px" pt="0px" pb="25px">
              <Flex px="25px" mb="25px" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={4}>
                <HStack spacing="15px" flexWrap="wrap">
                  <InputGroup maxW="300px">
                    <InputLeftElement>
                      <SearchIcon color="gray.400" />
                    </InputLeftElement>
                    <Input
                      placeholder="جستجو در کاربران..."
                      borderRadius="15px"
                      fontSize="sm"
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                    />
                  </InputGroup>
                  <Select
                    placeholder="فیلتر نقش"
                    borderRadius="15px"
                    fontSize="sm"
                    maxW="200px"
                    value={roleFilter}
                    onChange={(e) => handleRoleFilter(e.target.value)}
                  >
                    <option value="admin">مدیر</option>
                    <option value="manager">مدیر کل</option>
                    <option value="consultant">مشاور</option>
                    <option value="client">مراجع</option>
                  </Select>
                  <Select
                    placeholder="فیلتر وضعیت"
                    borderRadius="15px"
                    fontSize="sm"
                    maxW="200px"
                    value={statusFilter}
                    onChange={(e) => handleStatusFilter(e.target.value)}
                  >
                    <option value="all">همه کاربران</option>
                    <option value="active">فقط فعال</option>
                    <option value="inactive">فقط غیرفعال</option>
                  </Select>
                </HStack>
                <Button
                  leftIcon={<FaPlus />}
                  colorScheme="teal"
                  borderRadius="15px"
                  fontSize="sm"
                  fontWeight="bold"
                  px="20px"
                  py="10px"
                  onClick={handleAddUser}
                >
                  افزودن کاربر جدید
                </Button>
              </Flex>
              
              {/* جدول کاربران */}
              <Box overflowX={{ sm: "scroll", xl: "hidden" }}>
                <Table variant="simple" color={textColor} size="sm">
                  <Thead>
                    <Tr my=".8rem" pl="0px" color="gray.400">
                      <Th borderColor={borderColor} textAlign="right">
                        کاربر
                      </Th>
                      <Th borderColor={borderColor} textAlign="right">
                        نقش
                      </Th>
                      <Th borderColor={borderColor} textAlign="right">
                        اطلاعات تماس
                      </Th>
                      <Th borderColor={borderColor} textAlign="right">
                        آخرین ورود
                      </Th>
                      <Th borderColor={borderColor} textAlign="right">
                        وضعیت
                      </Th>
                      <Th borderColor={borderColor} textAlign="right">
                        عملیات
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {users.length === 0 ? (
                      <Tr>
                        <Td colSpan={6} textAlign="center" py={8}>
                          <Text color="gray.500">هیچ کاربری یافت نشد</Text>
                        </Td>
                      </Tr>
                    ) : (
                      users.map((user) => (
                        <Tr 
                          key={user._id || user.id}
                          opacity={user.isActive === false ? 0.6 : 1}
                          bg={user.isActive === false ? 'gray.50' : 'transparent'}
                        >
                          <Td minWidth={{ sm: "250px" }} pl="0px">
                            <Flex align="center" py=".8rem" minWidth="100%" flexWrap="nowrap">
                              <Avatar
                                size="md"
                                name={user.name || `${user.firstName} ${user.lastName}`}
                                bg="teal.500"
                                me="18px"
                              />
                              <Flex direction="column">
                                <Text
                                  fontSize="md"
                                  color={textColor}
                                  fontWeight="bold"
                                  minWidth="100%"
                                >
                                  {user.name || `${user.firstName} ${user.lastName}`}
                                </Text>
                                <Text fontSize="sm" color="gray.400" fontWeight="normal">
                                  {user.email}
                                </Text>
                              </Flex>
                            </Flex>
                          </Td>
                          <Td>
                            <HStack spacing={2}>
                              <Icon as={getRoleIcon(user.role)} color={`${getRoleColor(user.role)}.500`} />
                              <Badge
                                bg={`${getRoleColor(user.role)}.400`}
                                color="white"
                                fontSize="16px"
                                p="3px 10px"
                                borderRadius="8px"
                              >
                                {getRoleText(user.role)}
                              </Badge>
                            </HStack>
                          </Td>
                          <Td>
                            <Flex direction="column">
                              <Text fontSize="sm" color={textColor}>
                                {user.email}
                              </Text>
                              <Text fontSize="sm" color="gray.400">
                                {user.phone || 'شماره تماس ثبت نشده'}
                              </Text>
                            </Flex>
                          </Td>
                          <Td>
                            {user.lastLogin ? (
                              <JalaliDateDisplay
                                date={user.lastLogin}
                                format="jYYYY/jMM/jDD HH:mm"
                                textProps={{
                                  fontSize: "md",
                                  color: textColor,
                                  fontWeight: "bold"
                                }}
                              />
                            ) : (
                              <Text fontSize="md" color="gray.400" fontWeight="bold" pb=".5rem">
                                بدون ورود
                              </Text>
                            )}
                          </Td>
                          <Td>
                            <Flex direction="column" align="center">
                              <Tooltip 
                                label={currentUser?.role === 'admin' && user.role === 'manager' ? 'شما اجازه تغییر وضعیت مدیر کل را ندارید' : 'تغییر وضعیت کاربر'}
                                placement="top"
                              >
                                <Switch
                                  isChecked={user.isActive !== false}
                                  onChange={() => handleToggleUserStatus(user._id || user.id)}
                                  colorScheme="teal"
                                  isDisabled={toggleUserStatusMutation.isLoading || (currentUser?.role === 'admin' && user.role === 'manager')}
                                />
                              </Tooltip>
                              <Text fontSize="xs" color="gray.500" mt={1}>
                                {user.isActive !== false ? 'فعال' : 'غیرفعال'}
                              </Text>
                            </Flex>
                          </Td>
                          <Td>
                            <HStack spacing="2">
                              <Tooltip 
                                label={currentUser?.role === 'admin' && user.role === 'manager' ? 'شما اجازه ویرایش مدیر کل را ندارید' : 'ویرایش کاربر'}
                                placement="top"
                              >
                                <Button
                                  p="0px"
                                  bg="transparent"
                                  variant="no-hover"
                                  size="sm"
                                  onClick={() => handleEditUser(user)}
                                  isDisabled={currentUser?.role === 'admin' && user.role === 'manager'}
                                >
                                  <Text
                                    fontSize="md"
                                    color={currentUser?.role === 'admin' && user.role === 'manager' ? 'gray.300' : 'gray.400'}
                                    fontWeight="bold"
                                    cursor={currentUser?.role === 'admin' && user.role === 'manager' ? 'not-allowed' : 'pointer'}
                                  >
                                    ویرایش
                                  </Text>
                                </Button>
                              </Tooltip>
                              
                              {/* دکمه حذف - فقط برای مدیر کل */}
                              {currentUser?.role === 'manager' && (
                                <Tooltip 
                                  label="حذف کاربر"
                                  placement="top"
                                >
                                  <Button
                                    p="0px"
                                    bg="transparent"
                                    variant="no-hover"
                                    size="sm"
                                    onClick={() => {
                                      if (window.confirm('آیا از حذف این کاربر اطمینان دارید؟')) {
                                        deleteUserMutation.mutate(user._id || user.id);
                                      }
                                    }}
                                    isDisabled={deleteUserMutation.isLoading}
                                  >
                                    <Text
                                      fontSize="md"
                                      color="red.400"
                                      fontWeight="bold"
                                      cursor="pointer"
                                    >
                                      حذف
                                    </Text>
                                  </Button>
                                </Tooltip>
                              )}
                            </HStack>
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>

              {/* Pagination */}
              {totalItems > 0 && (
                <Flex
                  mt={4}
                  px="25px"
                  py={3}
                  justify="space-between"
                  align="center"
                  flexWrap="wrap"
                  gap={3}
                  borderTopWidth="1px"
                  borderColor={borderColor}
                >
                  <Text fontSize="sm" color="gray.500">
                    {totalItems} کاربر · صفحه {page} از {totalPages}
                  </Text>
                  <HStack spacing={2}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      isDisabled={page <= 1}
                    >
                      قبلی
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      isDisabled={page >= totalPages}
                    >
                      بعدی
                    </Button>
                  </HStack>
                </Flex>
              )}
            </Box>
          </Box>
        </Box>
      </Flex>

      {/* Modal فرم کاربر */}
      <CustomModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingUser ? 'ویرایش کاربر' : 'افزودن کاربر جدید'}
        onSubmit={() => {
          console.log('Modal onSubmit called');
          console.log('formRef.current:', formRef.current);
          
          const formData = formRef.current?.getValues();
          console.log('formData:', formData);
          
          if (formData) {
            console.log('Calling handleSubmitForm with:', formData);
            handleSubmitForm(formData);
          } else {
            console.log('formData is null or undefined');
            toast.error('خطا در دریافت اطلاعات فرم');
          }
        }}
        isLoading={createUserMutation.isLoading || updateUserMutation.isLoading}
        submitText={editingUser ? 'بروزرسانی' : 'افزودن'}
        size="xl"
      >
        <UserForm
          ref={formRef}
          user={editingUser}
          onSubmit={handleSubmitForm}
          isLoading={createUserMutation.isLoading || updateUserMutation.isLoading}
        />
      </CustomModal>
    </>
  );
}

export default Users; 